import { Tag } from '@dev-spendesk/grapes';

import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { useModal } from 'src/core/common/hooks/useModalGrapes';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';

import { DimensionApprovalFlowPanelContent } from './DimensionApprovalFlowPanelContent';
import { NodeDeleteConfirmationModal } from './NodeDeleteConfirmationModal';
import { useApprovalSchemeQuery } from '../../hooks/useApprovalScheme';
import { type CustomNode } from '../../node';
import { useWorkflowContext } from '../../state/WorkflowContext';
import { FloatingPanel } from '../FloatingPanel/FloatingPanel';
import { StandardApprovalFlowPanelContent } from '../StandardApprovalFlowPanelContent';

export const NodeSettingsPanel = () => {
  const { selectedNodeId, nodes, setSelectedNodeId, deleteNode, newNodeId } =
    useWorkflowContext();

  const currentNode = nodes.find((node) => node.id === selectedNodeId);
  const isOpen = Boolean(selectedNodeId && currentNode);
  const approvalSchemeId = currentNode ? getSchemeId(currentNode) : undefined;
  const panelType = getPanelType(currentNode?.type);
  const { t } = useTranslation('global');

  const approvalSchemeQueryState = useApprovalSchemeQuery(approvalSchemeId);
  const approvalScheme = unwrapQuery(approvalSchemeQueryState);
  const mode =
    currentNode?.type === 'baseNode' && currentNode.id === newNodeId
      ? 'create'
      : 'edit';

  const handleClosePanel = () => {
    // If the node is new, close the panel and delete it
    setSelectedNodeId(null, true);
    if (selectedNodeId && mode === 'create') {
      deleteNode(selectedNodeId);
    }
  };

  const handleSubmitPanel = () => {
    setSelectedNodeId(null, true);
  };

  const handleDeleteNode = () => {
    if (selectedNodeId) {
      deleteNode(selectedNodeId);
      hideNodeDeleteConfirmationModal();
    }
  };

  const [
    nodeDeleteConfirmationModal,
    showNodeDeleteConfirmationModal,
    hideNodeDeleteConfirmationModal,
  ] = useModal(({ isOpen: isModalOpened }) => (
    <NodeDeleteConfirmationModal
      isOpen={isModalOpened}
      onConfirm={handleDeleteNode}
      onCloseModal={() => hideNodeDeleteConfirmationModal()}
    />
  ));

  return (
    <>
      {nodeDeleteConfirmationModal}
      <FloatingPanel
        title={getPanelTitle(panelType, mode, t)}
        isOpen={isOpen}
        onClose={handleClosePanel}
        onDelete={
          currentNode && currentNode.type === 'baseNode'
            ? showNodeDeleteConfirmationModal
            : undefined
        }
      >
        {panelType === 'standardApprovalFlow' && (
          <StandardApprovalFlowPanelContent
            approvalSchemeId={approvalSchemeId}
            approvalRules={approvalScheme?.rules ?? []}
            onClose={handleClosePanel}
          />
        )}
        {panelType === 'dimensionApprovalFlow' && (
          <DimensionApprovalFlowPanelContent
            approvalRules={approvalScheme?.rules ?? undefined}
            approvalSchemeId={approvalSchemeId}
            mode={mode}
            selectedValues={
              currentNode?.type === 'baseNode'
                ? currentNode.data.values
                : undefined
            }
            dimension={
              currentNode?.type === 'baseNode'
                ? currentNode.data.dimension
                : undefined
            }
            onCancel={handleClosePanel}
            onSubmit={handleSubmitPanel}
          />
        )}
      </FloatingPanel>
    </>
  );
};

const getPanelTitle = (
  panelType: 'standardApprovalFlow' | 'dimensionApprovalFlow',
  mode: 'edit' | 'create',
  t: TGlobalFunctionTyped,
) => {
  const title =
    mode === 'edit'
      ? t('approvalWorkflows.edit')
      : t('approvalWorkflows.create');
  if (panelType === 'standardApprovalFlow') {
    return (
      <div className="flex items-center gap-16">
        {title}
        <Tag variant="ocean" iconName="magic-wand">
          {t('approvalWorkflows.standardApprovalFlow.defaultApprovers')}
        </Tag>
      </div>
    );
  }
  return title;
};

const getSchemeId = (node: CustomNode) => {
  if (node.type === 'standardApprovalFlowNode' || node.type === 'baseNode') {
    return node.data.schemeId;
  }
  return undefined;
};

const getPanelType = (
  nodeType:
    | 'labelNode'
    | 'standardApprovalFlowNode'
    | 'baseNode'
    | 'newConditionNode'
    | undefined,
) => {
  if (nodeType === 'standardApprovalFlowNode') {
    return 'standardApprovalFlow';
  }
  return 'dimensionApprovalFlow';
};
